import { useEffect, useMemo, useState } from "react";
import {
  isCallEnd,
  isCallEvent,
  isCallStart,
  type MessageKind,
} from "@ano/shared";
import { CALL_ACTIVE_WINDOW_MS } from "./message-area-types";

interface MessageWithKind {
  id: string;
  kind?: string | null;
  created_at: number;
}

/**
 * Find the active call message: the last "started a call" with no
 * subsequent "Call ended". Calls older than 5 minutes are considered
 * stale — "Call ended" may have been lost (browser fallback, Tauri
 * window event not firing, user left without closing). Users can
 * still join via the header Audio/Video buttons after the Join
 * badge expires.
 *
 * Re-evaluates every 10 seconds while a call is active so the badge
 * disappears promptly when staleness kicks in.
 */
export function useActiveCall(messageList: readonly MessageWithKind[]) {
  const [callStaleNowMs, setCallStaleNowMs] = useState(() => Date.now());

  const activeCallMessageId = useMemo(() => {
    const now = callStaleNowMs;
    let lastCallStartId: string | null = null;
    for (const msg of messageList) {
      const kind = ((msg.kind as MessageKind | null | undefined) ??
        "text") as MessageKind;
      if (!isCallEvent(kind)) continue;
      if (isCallStart(kind)) {
        if (now - msg.created_at < CALL_ACTIVE_WINDOW_MS) {
          lastCallStartId = msg.id;
        }
      } else if (isCallEnd(kind)) {
        lastCallStartId = null;
      }
    }
    return lastCallStartId;
  }, [messageList, callStaleNowMs]);

  // Tick every 10s while a call appears active to catch staleness promptly.
  // The seed-on-mount setState is intentional: when a call first appears,
  // we want `now` synchronized with that frame so the 5-minute window
  // calculation isn't off by however long the user has been idle.
  useEffect(() => {
    if (!activeCallMessageId) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setCallStaleNowMs(Date.now());
    const timer = setInterval(() => {
      setCallStaleNowMs(Date.now());
    }, 10_000);
    return () => clearInterval(timer);
  }, [activeCallMessageId]);

  return activeCallMessageId;
}
